<template>
    <div class="dropdown" id="app-lot-status">
        <a :class="`${dropdownClass} btn border-0 btn-secondary dropdown-toggle d-inline-flex align-items-center text-capitalize`"
            type="button" id="dropdownMenuButton" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
            <span class="pr-2"> {{ status }} </span>
            <app-icon name="chevron-down" class="size-16" />
        </a>

        <div class="dropdown-menu" aria-labelledby="dropdownMenuButton" id="dropdownMenu">
            <a class="dropdown-item py-3 d-flex align-items-center" style="cursor: pointer;"
                @click="changeInternalTransfer({ status_id: status.id, name: status.translated_name })"
                v-for="status in statuses" :key="status.id" v-if="status.id !== rowData.status_id">
                <span style="width: 10px; height: 10px;" :class="`rounded-circle bg-${status.class} d-inline-block`"></span>
                <span class="item-text-content d-block pl-3">
                    {{ status.translated_name }}
                </span>
            </a>
        </div>

        <app-confirmation-modal v-if="confirmationModalActive" icon="info"
            modal-id="app-status-change-confirmation-modal" :message="confirmationMessage"
            @confirmed="lotStatusChangeConfirm" @cancelled="lotStatusChangeCancel" />

        <app-order-error-modal v-if="orderErrorMessage" icon="info"
            modal-id="app-status-change-confirmation-modal" :message="confirmationMessage"
            @confirmed="lotStatusChangeConfirm" @cancelled="lotStatusChangeCancel" />

        <div v-if="revisedModalActive"
            style="position:fixed; top:0; left:0; right:0; bottom:0; background:rgba(0,0,0,0.5); display:flex; align-items:center; justify-content:center; z-index:1085;"
            @click.self="cancelRevisedDates">
            <div style="background:#fff; padding:22px; border-radius:6px; max-width:520px; width:92%; max-height:88%; overflow:auto;" class="text-left">
                <h5 class="mb-1">{{ $t('Set Revised Target Date') }}</h5>
                <p class="text-muted small mb-3">{{ $t('Moving this order to Pending. Enter a revised target date for each item. The original target date is retained.') }}</p>
                <div v-if="!revisedItems.length" class="text-muted small mb-3">{{ $t('No custom items found for this order.') }}</div>
                <div v-for="(it, idx) in revisedItems" :key="idx" class="mb-3">
                    <div class="font-weight-bold">{{ it.name }}</div>
                    <div class="small text-muted mb-1">{{ $t('Original Target Date') }}: {{ it.target_date || '—' }}</div>
                    <input type="date" class="form-control" :min="todayStr" v-model="it.revised_target_date" />
                </div>
                <div class="text-right mt-3">
                    <button type="button" class="btn btn-secondary mr-2" @click="cancelRevisedDates">{{ $t('Cancel') }}</button>
                    <button type="button" class="btn btn-primary" :disabled="!revisedItems.length" @click="submitRevisedDates">{{ $t('Save & Set Pending') }}</button>
                </div>
            </div>
        </div>

    </div>
</template>

<script>
import { mapState } from "vuex";
import HelperMixin from "../../../../../../common/Mixin/Global/HelperMixin";
import { axiosGet, axiosPost } from "../../../../../../common/Helper/AxiosHelper";
import { CHANGE_ORDER_STATUS, SELECTABLE_ORDER_STATUSES, SET_REVISED_TARGET_DATES } from "../../../../../Config/ApiUrl-CP";

export default {
    mixins: [HelperMixin],
    name: 'app-internal-transfer-status',
    props: ['rowData', 'tableId', 'value'],
    data() {
        return {
            confirmationMessage: '',
            confirmationModalActive: false,
            orderErrorMessage : false,
            lotStatusChangeEndpoint: '',
            lotStatusChangePostBody: {},
            status: '',
            statuses: [],
            revisedModalActive: false,
            revisedItems: [],
            pendingStatusChange: null,
        };
    },
    watch: {
        rowData(newValue) {
            if (newValue && newValue.status) {
                this.status = newValue.status.translated_name;
                console.log('rowData received:', this.status);
            }
        }
    },
    mounted() {
        
        this.status = this.rowData?.status?.translated_name ?? '';
        axiosGet(SELECTABLE_ORDER_STATUSES)
            .then(data => {
                this.statuses = data.data
            });
    },
    methods: {
        // ✔ Delivery Validation Logic

        // Returns the list of deliverable items that still lack a Collection/Dispatch
        // date. Canceled items are exempt. An empty list means the order can be Completed.
        validateAllDeliveryDetails() {
            const orderProducts = (this.rowData && this.rowData.order_products) || [];
            const items = orderProducts.filter(i => i?.variant?.product?.main_category != 4);
            const missing = [];
            for (const item of items) {
                if (item.delivery_status_id === 73) continue; // canceled → exempt
                const d = item.order_product_delivery_details;
                const hasDate = d && (d.collection_date || d.dispatch_date);
                if (!hasDate) {
                    missing.push((item && item.variant && item.variant.name) || ('Item #' + item.id));
                }
            }
            return missing;
        },

        changeInternalTransfer(newStatus) {

            // On Hold -> Pending: force a Revised Target Date for each custom item
            // (the original target date is retained for the patient's confirmation doc).
            if (this.status.toLowerCase() === 'on hold' && newStatus.name.toLowerCase() === 'pending') {
                const customs = (this.rowData.order_products || []).filter(i => i?.variant?.product?.main_category == 3);
                this.revisedItems = customs.map(i => ({
                    order_product_id: i.id,
                    name: i?.variant?.name || '',
                    target_date: i?.order_product_delivery_details?.target_date || '',
                    revised_target_date: '',
                }));
                this.pendingStatusChange = newStatus;
                this.revisedModalActive = true;
                return;
            }

            const endpoint = `${CHANGE_ORDER_STATUS}${this.rowData.id}`;
            const postBody = { status_id: newStatus.status_id };

            // 🔍 If user selects "Completed", every non-canceled deliverable item must
            // have a Collection or Dispatch date.
            if (newStatus.name.toLowerCase() === "completed") {
                const missing = this.validateAllDeliveryDetails();
                if (missing.length) {
                    this.$toastr.e(this.$t('Please enter a Collection or Dispatch date for these items before marking the order Completed:') + ' ' + missing.join(', '));
                    return; // Stop here
                }
            }

            this.lotStatusChangeEndpoint = endpoint;
            this.lotStatusChangePostBody = postBody;

            // Set confirmation message
            this.confirmationMessage =
                (newStatus.name.toLowerCase() === "delivered")
                    ? this.$t('If you change the status to "Delivered" you cannot modify it later')
                    : this.$t('normal_internal_transfer_status_change_message');

            this.confirmationModalActive = true;
        },
        async lotStatusChangeConfirm() {
            axiosPost(this.lotStatusChangeEndpoint, this.lotStatusChangePostBody)
                .then(response => {
                    setTimeout(() => {
                        window.location.reload()
                    }, 1500)
                    this.toastAndReload(response.data.message);
                    // Do something after successful data update, if needed
                })
                .catch(error => this.$toastr.e('', error.response.data.message));

            // calling the cancel method here as it closes the modal
            this.lotStatusChangeCancel();
        },
        lotStatusChangeCancel() {
            this.confirmationMessage = '';
            this.confirmationModalActive = false;
            this.lotStatusChangeEndpoint = '';
            this.lotStatusChangePostBody = {};
            this.orderErrorMessage = false;
        },
        cancelRevisedDates() {
            this.revisedModalActive = false;
            this.revisedItems = [];
            this.pendingStatusChange = null;
        },
        async submitRevisedDates() {
            for (const it of this.revisedItems) {
                if (!it.revised_target_date) {
                    this.$toastr.e(this.$t('Please set a Revised Target Date for every item.'));
                    return;
                }
            }
            try {
                await axiosPost(`${SET_REVISED_TARGET_DATES}${this.rowData.id}`, {
                    items: this.revisedItems.map(i => ({
                        order_product_id: i.order_product_id,
                        revised_target_date: i.revised_target_date,
                    })),
                });
                const response = await axiosPost(`${CHANGE_ORDER_STATUS}${this.rowData.id}`, {
                    status_id: this.pendingStatusChange.status_id,
                });
                this.revisedModalActive = false;
                this.toastAndReload((response && response.data && response.data.message) || 'Updated');
                setTimeout(() => window.location.reload(), 1200);
            } catch (error) {
                this.$toastr.e('', (error.response && error.response.data && error.response.data.message) || 'Error');
            }
        }
    },
    computed: {
        todayStr() {
            const d = new Date();
            const p = (n) => String(n).padStart(2, '0');
            return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
        },
        dropdownClass() {
            if (this.status.toLowerCase() === 'pending') return 'warning';
            if (this.status.toLowerCase() === 'on hold') return 'danger';
            if (this.status.toLowerCase() === 'work in progress') return 'warning';
            if (this.status.toLowerCase() === 'ready for trial') return 'purple';
            if (this.status.toLowerCase() === 'completed') return 'success';
            if (this.status.toLowerCase() === 'delivered') return 'success';
            if (this.status.toLowerCase() === 'canceled') return 'danger';
        },
    },
};
</script>

<style lang="scss" scoped>
#dropdownMenuButton {
    border-radius: 35px;
    font-size: 0.35rem;
    padding: 0.4rem 0.85rem;
    transform: scale(0.85);

    &.success {
        background: rgba(39, 174, 96, 0.2);
        color: #27ae60;
    }

    &.warning {
        background: rgba(255, 148, 23, 0.2);
        color: #ff9417;
    }

    &.purple {
        // background: rgba(128, 0, 128, 0.4);
        background: #964ed8;
        color: #ffffff;
    }

    &.danger {
        background: rgba(252, 44, 16, 0.2);
        color: #fc2c10;
    }
}

.pe-none {
    pointer-events: none;
}
</style>
